import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, MessageCircle, HandHeart, Sparkles, ArrowUpRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";

const PACKAGES = [
    {
        key: "accompagnement_essentiel_monthly",
        tier: "accompagnement_essentiel",
        name: "Essentiel",
        price: 120,
        subtitle: "Pour poser une intention et se laisser accompagner mois après mois.",
        features: [
            "1 séance de soin énergétique par mois",
            "1 session de connexion à ses guides par mois",
            "Suivi personnalisé par messagerie",
            "-10€ automatiques sur tout soin de lumière ou séance de connexion à ses guides additionnel",
        ],
    },
    {
        key: "accompagnement_profond_monthly",
        tier: "accompagnement_profond",
        name: "Profond",
        price: 220,
        subtitle: "Un cheminement soutenu, pour transformer durablement.",
        features: [
            "2 séances de soin énergétique par mois",
            "2 sessions de connexion à ses guides par mois",
            "Suivi personnalisé par messagerie",
            "-10€ automatiques sur tout soin de lumière ou séance de connexion à ses guides additionnel",
        ],
        highlight: true,
    },
];

export default function AccompagnementsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [dialog, setDialog] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [waitlistForms, setWaitlistForms] = useState({});
    const [waitlistBusy, setWaitlistBusy] = useState(null);

    useEffect(() => {
        http.get("/accompagnements/availability").then((r) => setAvailability(r.data)).catch(() => {});
    }, []);

    const openPaymentDialog = (key) => {
        if (!user || user === false) {
            toast.info("Créez un compte pour souscrire.");
            navigate("/inscription");
            return;
        }
        const p = PACKAGES.find((x) => x.key === key);
        setDialog({
            kind: "subscription",
            lookup_key: key,
            item_name: `Accompagnement ${p.name}`,
            amount: p.price * 100,
            origin_url: window.location.origin,
        });
    };

    const stripeCheckout = async () => {
        const { data } = await http.post("/payments/checkout", {
            lookup_key: dialog.lookup_key,
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    const submitWaitlist = async (tier) => {
        const f = waitlistForms[tier] || {};
        if (!f.email) return toast.error("Merci d'indiquer votre email.");
        setWaitlistBusy(tier);
        try {
            const { data } = await http.post("/accompagnements/waitlist", {
                tier, email: f.email.trim(), name: (f.name || "").trim(),
            });
            toast.success(data.already_registered
                ? "Vous êtes déjà sur la liste — je vous préviens dès qu'une place se libère."
                : "Vous êtes inscrit(e) sur la liste d'attente. Je vous préviens par email.");
            setWaitlistForms((s) => ({ ...s, [tier]: { email: "", name: "" } }));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setWaitlistBusy(null);
        }
    };

    return (
        <div className="max-w-6xl mx-auto px-6 py-24" data-testid="accompagnements-page">
            <Seo
                title="Accompagnements · Foundation of Light"
                description="Accompagnements individuels sur plusieurs séances de soins de lumière avec le langage de lumière, guidance et connexion aux guides. Places limitées."
                path="/accompagnements"
            />
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center mb-6">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Accompagnements sur mesure</div>
                <h1 className="font-serif text-5xl sm:text-6xl mb-4">Un chemin, un présent, un guide</h1>
                <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                    Deux formats pour cheminer sur la durée, alliant séances énergétiques et
                    suivi personnalisé par messagerie. Résiliable à tout moment.
                </p>
                <p className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary/80 bg-primary/5 rounded-full px-4 py-1.5" data-testid="accompagnement-limited-note">
                    <Sparkles className="w-3 h-3" /> Le nombre de places est limité
                </p>
            </motion.div>

            <div className="rounded-3xl border border-secondary/40 bg-gradient-to-br from-secondary/10 to-transparent p-8 md:p-10 mb-14 relative">
                <div className="absolute -top-3 left-8 bg-secondary text-secondary-foreground text-[10px] uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1 shadow-sm">
                    <Sparkles className="w-3 h-3" /> Notre conseil
                </div>
                <h2 className="font-serif text-2xl md:text-3xl mb-4">Commencez par un nettoyage en profondeur</h2>
                <p className="text-base text-foreground/80 leading-relaxed mb-4">
                    Avant de vous engager dans un accompagnement régulier étalé sur plusieurs mois, nous vous
                    invitons chaleureusement à suivre d'abord un <strong>Programme accéléré</strong> — quatre
                    séances de soins de lumière condensées sur un mois. Cet élan initial permet d'opérer un
                    nettoyage de fond prioritaire, de dissoudre les charges anciennes, et d'ouvrir un
                    terrain fertile pour la suite du chemin.
                </p>
                <p className="text-base text-foreground/80 leading-relaxed mb-6">
                    Une fois cette phase intense traversée, vous pourrez cheminer plus sereinement, à un
                    rythme plus doux et espacé dans le temps, avec l'un de nos accompagnements ci-dessous.
                </p>
                <a
                    href="/reservation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full h-11 px-5 bg-primary text-primary-foreground text-sm font-medium hover:scale-[1.02] transition-transform"
                    data-testid="pre-accompagnement-cta"
                >
                    Découvrir le Programme accéléré
                    <ArrowUpRight className="w-4 h-4" />
                </a>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {PACKAGES.map((p, i) => {
                    const av = availability?.[p.tier];
                    const soldOut = av && av.available <= 0;
                    const wf = waitlistForms[p.tier] || { email: "", name: "" };
                    return (
                        <motion.div
                            key={p.key}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className={`rounded-3xl border p-8 md:p-10 ${
                                p.highlight ? "border-secondary bg-card shadow-2xl" : "border-border/60 bg-card"
                            }`}
                            data-testid={`accompagnement-${p.key}`}
                        >
                            <div className="flex gap-3 mb-6">
                                <div className="p-2 rounded-full bg-primary/10"><HandHeart className="w-4 h-4 text-primary" /></div>
                                <div className="p-2 rounded-full bg-secondary/15"><MessageCircle className="w-4 h-4 text-secondary" /></div>
                            </div>
                            <h3 className="font-serif text-4xl mb-2">{p.name}</h3>
                            <p className="text-sm text-muted-foreground mb-6">{p.subtitle}</p>

                            {av && (
                                <div
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs mb-5 ${
                                        soldOut
                                            ? "bg-destructive/10 text-destructive"
                                            : av.available <= 2
                                                ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                                : "bg-primary/10 text-primary"
                                    }`}
                                    data-testid={`availability-${p.tier}`}
                                >
                                    {soldOut
                                        ? "Plus de place disponible actuellement"
                                        : `${av.available} place${av.available > 1 ? "s" : ""} disponible${av.available > 1 ? "s" : ""} actuellement`}
                                </div>
                            )}

                            <div className="mb-8">
                                <span className="font-serif text-6xl">{p.price}€</span>
                                <span className="text-muted-foreground ml-2 text-sm">/ mois</span>
                            </div>
                            <ul className="space-y-3 mb-10">
                                {p.features.map((f) => (
                                    <li key={f} className="flex items-start gap-3 text-sm">
                                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>

                            {soldOut ? (
                                <div className="rounded-2xl border border-border/60 bg-muted/40 p-5" data-testid={`waitlist-form-${p.tier}`}>
                                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                                        <Mail className="w-4 h-4 text-primary" />
                                        Liste d'attente
                                    </div>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Laissez-moi votre email — je vous préviens dès qu'une place se libère.
                                    </p>
                                    <div className="space-y-2">
                                        <Input
                                            type="text"
                                            placeholder="Votre prénom (facultatif)"
                                            value={wf.name}
                                            onChange={(e) => setWaitlistForms((s) => ({ ...s, [p.tier]: { ...wf, name: e.target.value } }))}
                                            className="rounded-full h-10"
                                            data-testid={`waitlist-name-${p.tier}`}
                                        />
                                        <Input
                                            type="email"
                                            placeholder="Votre email"
                                            value={wf.email}
                                            onChange={(e) => setWaitlistForms((s) => ({ ...s, [p.tier]: { ...wf, email: e.target.value } }))}
                                            className="rounded-full h-10"
                                            data-testid={`waitlist-email-${p.tier}`}
                                        />
                                        <Button
                                            onClick={() => submitWaitlist(p.tier)}
                                            disabled={waitlistBusy === p.tier}
                                            className="w-full rounded-full h-10 bg-primary text-primary-foreground"
                                            data-testid={`waitlist-submit-${p.tier}`}
                                        >
                                            {waitlistBusy === p.tier ? "Envoi…" : "Me prévenir dès qu'une place se libère"}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    onClick={() => openPaymentDialog(p.key)}
                                    className={`w-full rounded-full h-12 ${
                                        p.highlight ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground"
                                    } hover:scale-[1.02] transition-transform`}
                                    data-testid={`subscribe-accompagnement-${p.key}`}
                                >
                                    Choisir {p.name}
                                </Button>
                            )}
                        </motion.div>
                    );
                })}
            </div>
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={stripeCheckout}
            />
        </div>
    );
}
